export function hexToHsl(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16) / 255
  const g = parseInt(hex.slice(3, 5), 16) / 255
  const b = parseInt(hex.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const l = (max + min) / 2
  let h = 0
  let s = 0
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
}

export function getContrastTextColor(hex: string): '#ffffff' | '#000000' {
  const hsl = hexToHsl(hex)
  const lightness = parseInt(hsl.split(' ')[2], 10)
  return lightness < 50 ? '#ffffff' : '#000000'
}

/**
 * True for colours pale enough to vanish against a white surface (white hold
 * tape, a white grade), which need an outline to stay visible.
 */
export function isLightColor(hex: string | null | undefined): boolean {
  if (!hex || !/^#[0-9a-fA-F]{6}$/.test(hex)) return false
  const lightness = parseInt(hexToHsl(hex).split(' ')[2], 10)
  return lightness >= 85
}

function hexToHslParts(hex: string): { h: number; s: number; l: number } | null {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return null
  const [h, s, l] = hexToHsl(hex).split(' ')
  return { h: parseInt(h, 10), s: parseInt(s, 10), l: parseInt(l, 10) }
}

/**
 * A soft-tint badge palette derived from one colour: a pale background and a
 * darker, same-hue text colour — the generic version of Tailwind's
 * `bg-*-100`/`text-*-700` pairing. Used for grade badges so the admin's grade
 * colour drives the chip while staying readable for any hue. Falls back to a
 * neutral grey pair when the colour is missing or malformed.
 */
export function softBadgeColors(hex: string | null | undefined): { bg: string; fg: string } {
  const parts = hex ? hexToHslParts(hex) : null
  if (!parts) return { bg: 'hsl(0 0% 93%)', fg: 'hsl(0 0% 25%)' }
  const { h, s } = parts
  return {
    bg: `hsl(${h} ${Math.min(s, 85)}% 92%)`,
    fg: `hsl(${h} ${Math.max(s, 45)}% 30%)`,
  }
}
